import { select, takeEvery } from 'redux-saga/effects';

import * as authSelectors from '@redux/reducers/auth';
import { store } from '@redux/storeConfig/store';
import camelcaseKeys from 'camelcase-keys';
import { normalize } from 'normalizr';
import * as entityActions from '../../Entities/actions';
import * as entitySelectors from '../../Entities/reducers';
import * as actions from '../actions';
import * as selectors from '../reducers';
import * as schemas from '../schemas';
import * as schemasImportedLocations from '../schemas/importLocations';
import {
  deleteLocationService,
  getLocationByIdService,
  getLocationsListService,
  getWorkplanExecutionLocationsService,
  importLocationsService,
  saveLocationService
} from '../services/locations';
import * as types from '../types';

/* -------------------------------------------------------------------------- */
/*                                 FETCH LIST                                 */
/* -------------------------------------------------------------------------- */
function* fetchLocationsList(action) {
  const {
    operatorId: filterOperatorId,
    organizationId: filterOrganizationId,
    groupId,
    bounds: filterBounds,
    workplanExecutionId,
    isPublic
  } = action.payload;
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });

  getLocationsListService(
    {
      isPublic: isPublic ? 1 : 0,
      operatorId: operatorId,
      // En modo público no aplican organizationId/divisionId/subdivisionId
      organizationId: isPublic ? null : organizationId,
      // El grupo solo se envía al backend cuando el usuario selecciona uno específico
      groupId: isPublic ? null : groupId ?? null,
      minLat: filterBounds?.minLat,
      maxLat: filterBounds?.maxLat,
      minLng: filterBounds?.minLng,
      maxLng: filterBounds?.maxLng,
      locationIds: filterBounds?.locationIds
        ? JSON.stringify(filterBounds?.locationIds)
        : filterBounds?.locationIds,
      workplanExecutionId: workplanExecutionId
    },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { locations },
          result //order
        } = normalize(resultData, schemas.locations); //normalize data to byId and order
        store.dispatch(
          actions.completeFetchingLocations(
            locations,
            result,
            operatorId,
            organizationId,
            filterBounds?.showInMapLocations ? filterBounds?.showInMapLocations : false,
            filterBounds === undefined ? true : false
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingLocations(error));
      }
    }
  );
}

export function* watchFetchLocationsList() {
  yield takeEvery(types.LOCATIONS_FETCH_STARTED, fetchLocationsList);
}

/* -------------------------------------------------------------------------- */
/*                             PARTIAL FETCH LIST                             */
/* -------------------------------------------------------------------------- */
function* partialFetchLocationsList(action) {
  const {
    entityTypeId,
    operatorId: filterOperatorId,
    organizationId: filterOrganizationId
  } = action.payload;
  const date = yield select(entitySelectors.getUserUpdateByEntityDate, entityTypeId);
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });

  getLocationsListService(
    { userFetchDate: date, operatorId, organizationId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        const {
          entities: { locations },
          result //order
        } = normalize(resultData, schemas.locations); //normalize data to byId and order
        store.dispatch(
          actions.completePartialFetchingLocations(locations, result, operatorId, organizationId)
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failPartialFetchingLocations(error));
      }
    }
  );
}

export function* watchPartialFetchLocationsList() {
  yield takeEvery(types.LOCATIONS_PARTIAL_FETCH_STARTED, partialFetchLocationsList);
}

/* -------------------------------------------------------------------------- */
/*                                     ADD                                    */
/* -------------------------------------------------------------------------- */
function* addLocation(action) {
  const location = action.payload;
  saveLocationService(
    { ...location, locationId: null },
    {
      successAction: (response) => {
        const addedLocation = camelcaseKeys(response.data);
        store.dispatch(actions.completeAddingLocation(location.locationId, addedLocation));
        store.dispatch(entityActions.startFetchingEntities());
      },
      errorAction: (error) => {
        store.dispatch(actions.failAddingLocation(location.locationId, error));
      }
    }
  );
}

export function* watchAddLocationsStarted() {
  yield takeEvery(types.LOCATION_ADD_STARTED, addLocation);
}

/* -------------------------------------------------------------------------- */
/*                                    EDIT                                    */
/* -------------------------------------------------------------------------- */
function* editLocation(action) {
  const location = action.payload;
  const { oldLocation } = yield select((state) =>
    selectors.getLocation(state, location.locationId)
  );
  saveLocationService(
    { ...location },
    {
      successAction: (response) => {
        const editedLocation = camelcaseKeys(response.data);
        store.dispatch(actions.completeEditingLocation(editedLocation));
        store.dispatch(entityActions.startFetchingEntities());
      },
      errorAction: (error) => {
        store.dispatch(actions.failEditingLocation(oldLocation, error));
      }
    }
  );
}

export function* watchEditLocationsStarted() {
  yield takeEvery(types.LOCATION_EDIT_STARTED, editLocation);
}

/* -------------------------------------------------------------------------- */
/*                                   REMOVE                                   */
/* -------------------------------------------------------------------------- */
function* removeLocation(action) {
  const { locationId } = action.payload;
  const location = yield select((state) => selectors.getLocation(state, locationId));
  deleteLocationService(
    { ...location },
    {
      successAction: (response) => {
        store.dispatch(actions.completeRemovingLocation(locationId));
      },
      errorAction: (error) => {
        store.dispatch(actions.failRemovingLocation(locationId, error));
      }
    }
  );
}

export function* watchRemoveLocationStarted() {
  yield takeEvery(types.LOCATION_REMOVE_STARTED, removeLocation);
}

/* -------------------------------------------------------------------------- */
/*                                    IMPORT                                  */
/* -------------------------------------------------------------------------- */
function* importLocations(action) {
  const locations = action.payload;
  const { operatorId: filterOperatorId, organizationId: filterOrganizationId } = action.payload;
  const { operatorId, organizationId } = yield select(authSelectors.getAuthUserMembershipFilters, {
    filterOperatorId,
    filterOrganizationId
  });
  importLocationsService(
    { ...locations },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        // Fetch locations
        store.dispatch(actions.startFetchingLocations({ operatorId, organizationId }));
        const {
          entities: { importedLocations },
          result //order
        } = normalize(resultData, schemasImportedLocations.importedLocations); //normalize data to byId and order
        // Complete import locations
        store.dispatch(actions.completeImportingLocations(importedLocations, result));
      },
      errorAction: (error) => {
        store.dispatch(actions.failImportingLocations(error));
      }
    }
  );
}

export function* watchImportLocationsStarted() {
  yield takeEvery(types.LOCATIONS_IMPORT_STARTED, importLocations);
}

/* -------------------------------------------------------------------------- */
/*                             GET LOCATION BY ID                             */
/* -------------------------------------------------------------------------- */

function* fetchLocationById(action) {
  const locationId = action.payload;
  getLocationByIdService(
    {
      locationId
    },
    {
      successAction: (response) => {
        const resultData = camelcaseKeys(response.data);
        store.dispatch(actions.completeFetchingLocation(resultData, [resultData.locationId]));
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingLocation(error));
      }
    }
  );
}

export function* watchFetchLocationById() {
  yield takeEvery(types.LOCATION_FETCH_STARTED, fetchLocationById);
}

/* -------------------------------------------------------------------------- */
/*                      FETCH WORKPLAN EXECUTION LOCATIONS                   */
/* -------------------------------------------------------------------------- */

function* fetchWorkplanExecutionLocations(action) {
  const { workplanExecutionId } = action.payload;

  getWorkplanExecutionLocationsService(
    { workplanExecutionId },
    {
      successAction: (response) => {
        const resultData = response.data.map((result) => camelcaseKeys(result));
        
        const {
          entities: { locations },
          result //order
        } = normalize(resultData, schemas.locations); //normalize data to byId and order
        
        store.dispatch(
          actions.completeFetchingWorkplanExecutionLocations(
            workplanExecutionId,
            locations,
            result
          )
        );
      },
      errorAction: (error) => {
        store.dispatch(actions.failFetchingWorkplanExecutionLocations(workplanExecutionId, error));
      }
    }
  );
}

export function* watchFetchWorkplanExecutionLocations() {
  yield takeEvery(types.WORKPLAN_EXECUTION_LOCATIONS_FETCH_STARTED, fetchWorkplanExecutionLocations);
}
